/**
 * Therapy display, colour and ORDERING rules for slots and prescription lines.
 *
 * This file owns FIVE distinct ordering axes — delivery/layout, chronological,
 * naming, canonical identity, and prescription order. They are not
 * interchangeable, and every ordering bug this system has shipped (MD-314,
 * MD-315, MD-411) was a surface answering one question with another axis.
 *
 * Before changing any order, or picking one for a new surface, read
 * `docs/therapy-ordering.md`. It carries the decision table, the invariants
 * each axis protects, and the list of places that still bypass them.
 *
 * Do not re-implement any of these at a call site: a sort inside a component
 * is a sixth axis nobody will find.
 */
import { TherapyDetailsDto, TherapyRxDto } from "../api-client";

/**
 * Anything that carries a scheduled treatment: a TimeSlotDto, a Treatment, or
 * one of the calendar's own slot shapes. Kept structural because the same
 * display rule applies to all of them.
 */
export interface SlotLike {
  therapy?: TherapyDetailsDto | null;
  therapyRx?: TherapyRxDto | null;
  /** Scheduledness: an unscheduled slot has no start. Deciding input — see slotTherapy. */
  start?: string | Date | null;
  /** The slot kind; only `SalesTimeSlot` changes a rule here — see soldService. */
  type?: string | null;
}

/**
 * MD-724: what a SALES slot (Verkauf) delivers — the sold service.
 *
 * A sale has no prescription line, no term and no performed line item
 * (`therapy` is a Treatment field). The API ships the service inside a
 * stand-in `therapyRx` (`{ therapy: service, rx: { patient } }`, physio-api
 * `mapAppointments`), the pre-MD-232 place every surface used to read. When
 * MD-232 moved the line item onto the slot, nothing read the stand-in any
 * more: a planned sale had no therapy, the calendar adapters drew it with
 * `display: "none"`, and it vanished from every calendar view.
 *
 * The service is a sale's line item in every sense these rules care about
 * (label, duration, colour identity), so it stands in wherever a Treatment's
 * `therapy` would. Keyed on the slot type: no other slot carries
 * `therapyRx.therapy` on purpose.
 */
export const soldService = (slot: SlotLike | null | undefined): TherapyDetailsDto | undefined => {
  if (slot?.type !== "SalesTimeSlot") return undefined;
  const sale = slot as SlotLike & {
    service?: TherapyDetailsDto | null;
    therapyRx?: { therapy?: TherapyDetailsDto | null } | null;
  };
  return sale.service ?? sale.therapyRx?.therapy ?? undefined;
};

/** A sold service (Verkauf) rather than a prescribed treatment. */
export const isSalesSlot = (slot: SlotLike | null | undefined): boolean =>
  slot?.type === "SalesTimeSlot";


/**
 * A key into the GetTherapyColors map. Branded so it cannot be confused with
 * the display text sitting next to it: the whole D-RX-05 class of bug was
 * surfaces indexing the colour map with whatever abbreviation they were about
 * to render. Build one with `methodColorKey` / `termColorKey`, never by hand.
 */
export type TherapyColorKey = string & { readonly __therapyColorKey: unique symbol };

/**
 * The two catalogs share natural-key ids — `KG`, `KGG`, `MT`, `BGM`, `CM`,
 * `PM`, `SM` and `ET` are each both a TherapyMethod id and a PrescribedTherapy
 * id — so the colour map namespaces them. Without the prefix, recolouring the
 * METHOD `KG` appeared to do nothing: the term's colour occupied the same key.
 */
export const methodColorKey = (id: string): TherapyColorKey =>
  `method:${id}` as TherapyColorKey;

export const termColorKey = (id: string): TherapyColorKey => `term:${id}` as TherapyColorKey;

const termDetails = (slot: SlotLike | null | undefined): TherapyDetailsDto | undefined => {
  const term = slot?.therapyRx?.prescribedTherapy;
  if (!term) return undefined;
  const abbreviation = term.shortLabel ?? term.label;
  return {
    // The everyday shorthand where curated; the official name as fallback.
    abbreviation,
    // MD-232: colours are looked up by IDENTITY. `abbreviation` above is display
    // text an admin can rename, so it must never reach the colour map — see
    // therapyColorKey below.
    colorKey: termColorKey(term.id),
    isSupplementary: term.isSupplementary,
    therapyType: term.therapyType ?? undefined,
    // `isPassive` is a line-item fact a term does not carry, but the calendar's
    // background-event rendering must survive a slot without a line item —
    // the entry-abbreviation stopgap decides (see PASSIVE_ABBREVIATIONS_AT_ENTRY).
    isPassive: PASSIVE_ABBREVIATIONS_AT_ENTRY.includes(abbreviation),
  } as TherapyDetailsDto;
};

/**
 * The therapy to show for a slot (MD-232).
 *
 * A slot's `therapy` is the billable LINE ITEM, resolved at scheduling time
 * from the prescribed term, the Rx's tarif group and the therapist's
 * credentials. SCHEDULEDNESS decides which level speaks, not the presence of
 * `therapy`:
 *
 * - Scheduled slot (has `start`): the line item. It is the live decision.
 * - Unscheduled slot: the TERM (shortLabel). Unscheduling deliberately does
 *   NOT nullify `slot.therapy` — the sales system reads it — so an unscheduled
 *   slot can carry a stale line item from its previous schedule. That value is
 *   history ("what it was, under that therapist's credentials"), not identity;
 *   what is to be scheduled is only the prescribed term. Trusting mere
 *   presence showed the debris.
 *
 * The term fallback deliberately carries no price, duration or id: those are
 * properties of a line item and do not exist before one is chosen. Callers
 * that need them must handle `undefined` rather than read a made-up value.
 *
 * A sale has neither line item nor term; its sold service speaks (MD-724,
 * see soldService).
 */
export const slotTherapy = (slot: SlotLike | null | undefined): TherapyDetailsDto | undefined => {
  const term = termDetails(slot);
  if (!slot?.start && term) return term;
  return slot?.therapy ?? term ?? soldService(slot);
};

/**
 * The line-item ids a drop can be credential-checked against IN THE CLIENT,
 * before the server sees it.
 *
 * Only slots WITHOUT a prescription line qualify. A slot that has one carries
 * a line item that was resolved for whoever last held it — history, not
 * identity (see above) — so testing the target therapist against it rejects
 * perfectly legal work: KG-ZNS resolved to `GKV_KGN BO` under Daniela says
 * nothing about Lennart, for whom the same prescribed term resolves to
 * `GKV_KGN PNF`. Only the server knows the term's candidate set, so those
 * slots are its decision: every scheduling verb re-resolves from the term and
 * answers 409 when the therapist can deliver no variant of it.
 */
export const locallyCheckableTherapyIds = (
  slots: readonly SlotLike[] | null | undefined
): string[] =>
  (slots ?? [])
    // A sold service is not gated on the therapist's credentials (physio-api
    // MD-724 skips sales in therapy resolution), so a sale is never checked.
    .filter((slot) => !slot?.therapyRx?.id && !isSalesSlot(slot))
    .map((slot) => slotTherapy(slot)?.id)
    .filter((id): id is string => !!id);

/**
 * The catalog IDENTITY a slot's colour is looked up by (GetTherapyColors) —
 * the TherapyMethod id for a scheduled slot, the PrescribedTherapy id for an
 * unscheduled one, following the same scheduledness rule as slotTherapy.
 *
 * MD-232: this exists so that no surface colours by `abbreviation`. That used
 * to work only because the seed data gives a method the same string for `id`,
 * `label` and its line items' `abbreviation`; renaming a Heilmittel broke the
 * coincidence and every affected event silently fell back to grey (D-RX-05).
 * Returns undefined when the slot carries no identity — callers render their
 * own fallback rather than guessing from text.
 */
export const therapyColorKey = (
  slot: SlotLike | null | undefined
): TherapyColorKey | undefined => {
  const therapy = slotTherapy(slot) as
    | { therapyMethodId?: string | null; colorKey?: TherapyColorKey | null }
    | undefined;
  if (therapy?.therapyMethodId) return methodColorKey(therapy.therapyMethodId);
  // Already namespaced by termDetails — the term branch never sees a bare id.
  return therapy?.colorKey ?? undefined;
};

/** Display text for a slot, by the same scheduledness rule as slotTherapy. */
export const slotTherapyLabel = (slot: SlotLike | null | undefined): string => {
  const term = termDetails(slot)?.abbreviation ?? "";
  if (!slot?.start && term) return term;
  return slot?.therapy?.abbreviation ?? (term || soldService(slot)?.abbreviation || "");
};

/**
 * Whether a slot is a passive preparatory application (Warmpackung, Heißluft)
 * — the patient receives it without the therapist working on them, so the
 * calendar orders it ahead of the active treatment in the same appointment.
 *
 * Reads the server's `isPassive` flag. Never test abbreviations for this: the
 * set is data, it differs per discipline, and Heiße Rolle is a Wärme therapy
 * that is NOT passive because it occupies the therapist.
 */
export const isPassiveTherapy = (
  therapy: { isPassive?: boolean } | null | undefined
): boolean => therapy?.isPassive === true;

/** Convenience: is this slot a passive preparatory application? */
export const isPassiveSlot = (slot: SlotLike | null | undefined): boolean =>
  isPassiveTherapy(slotTherapy(slot));

/**
 * Abbreviations of passive applications, for the places that cannot read
 * `isPassive` because no line item exists yet: the Rx editor's line-order
 * check (typed text), and template handles built from prescribed terms.
 * Everywhere a therapy object exists, use `isPassiveTherapy` — this list is a
 * stopgap, not a second source of truth.
 */
export const PASSIVE_ABBREVIATIONS_AT_ENTRY = ["PA", "TA", "HL"];

/**
 * Scheduling order for the therapies of ONE appointment: supplementary
 * applications (PA, BAN, HL, …) first — they prepare the patient for the main
 * therapy — then the main therapies, each side keeping its given relative
 * order. Every slot-layout path must walk THIS order; laying out in
 * prescription order instead puts the supplement after the main therapy,
 * which is the long-standing user complaint. The passive-shift that both
 * layout paths apply afterwards is what keeps the main therapy on the drop
 * point (passive preparation slots move ahead of it).
 */
export const supplementsFirst = <T>(items: T[], isSupplementary: (item: T) => boolean): T[] => [
  ...items.filter(isSupplementary),
  ...items.filter((item) => !isSupplementary(item)),
];

/**
 * NAMING order for the therapies of ONE appointment: main therapies first,
 * supplementary applications after, alphabetical within each side.
 *
 * DELIBERATELY THE MIRROR OF `supplementsFirst`. The two are different axes and
 * both are right:
 *
 *  - `supplementsFirst` is DELIVERY order — a Warmpackung prepares the patient,
 *    so it is applied before the main therapy. Every slot-LAYOUT path walks it.
 *  - this one is NAMING order — how the set is written down and read back.
 *    A Rezept lists the main Heilmittel first and the supplement after it, and
 *    that is how the practice has always read them: in the THEORG export it ran
 *    until 2023, slot 1 is the main therapy in all 10,672 prescriptions without
 *    a single exception, and PA/HR/BAN never appear earlier than slot 2 —
 *    "KG | PA", never "PA | KG".
 *
 * Alphabetical is on the RENDERED label, so the order the reader sees is
 * explained by the text they see. Both sides are sorted, so the same set of
 * therapies collapses to the same string regardless of what order the API
 * returned the slots in — which is the point: without it the same appointment
 * reads "KG · PA" on one surface and "PA · KG" on another (MD-315).
 */
export const therapyNamingOrder = <T>(
  items: readonly T[],
  isSupplementary: (item: T) => boolean,
  label: (item: T) => string
): T[] => {
  const byLabel = (a: T, b: T) => label(a).localeCompare(label(b), "de");
  return [
    ...items.filter((item) => !isSupplementary(item)).sort(byLabel),
    ...items.filter(isSupplementary).sort(byLabel),
  ];
};

/**
 * The slots of one appointment in DELIVERY order: supplementary applications
 * first (they prepare the patient), alphabetical within each side.
 *
 * The sibling of `slotsInNamingOrder` and its exact mirror — see that one for
 * why the two axes point opposite ways. Every slot-LAYOUT path walks this one.
 *
 * The alphabetical tie-break is what makes a layout REPRODUCIBLE. This used to
 * fall back to `therapyRx.order`, which is null in every environment, so within
 * each side the layout followed whatever order Postgres happened to return —
 * i.e. it changed whenever a row was updated (MD-315).
 */
export const slotsInDeliveryOrder = <T extends SlotLike>(
  slots: readonly T[] | null | undefined
): T[] => {
  const isSupplementary = (slot: T) => slotTherapy(slot)?.isSupplementary === true;
  const byLabel = (a: T, b: T) => slotTherapyLabel(a).localeCompare(slotTherapyLabel(b), "de");
  return supplementsFirst(
    [...(slots ?? [])].sort(byLabel),
    isSupplementary
  );
};

/**
 * The slots of one appointment in naming order. Generic so a caller's own slot
 * type survives — these go straight back into row components that need more
 * than `SlotLike` carries.
 */
export const slotsInNamingOrder = <T extends SlotLike>(
  slots: readonly T[] | null | undefined
): T[] =>
  therapyNamingOrder(
    slots ?? [],
    (slot) => slotTherapy(slot)?.isSupplementary === true,
    slotTherapyLabel
  );

/**
 * The slots of one appointment in the order they ACTUALLY RUN: by start time,
 * earliest first. The third axis, and the one a reader of a SCHEDULED
 * appointment needs.
 *
 * Naming order answers "how is this set written down"; this answers "what
 * happens first". The two agreed until 0.4.0, which is why one function served
 * both. MD-333 split them: a passive preparation is now deliberately laid out
 * BEFORE the active treatment, so "main therapy first" states the opposite of
 * what the clock says, and an appointment running PA-then-KG was read back as
 * "KG, PA" (MD-411).
 *
 * Reads the STORED times rather than re-deriving delivery order, deliberately.
 * Delivery order is what the layout paths INTEND; the times are what was
 * actually scheduled, and the two part company as soon as anyone drags a slot
 * out of the usual arrangement. A list claiming an order has to report the
 * second one — otherwise it hides the very case a reader would want to see.
 *
 * Falls back to naming order for an UNSCHEDULED appointment: nothing runs yet,
 * so there is no chronology to show and the naming axis stands unopposed. The
 * fallback is all-or-nothing per appointment, so a half-scheduled set never
 * interleaves two different orders.
 *
 * Ties break on the rendered label. A Doppelbehandlung is two slots sharing one
 * start, and without the tie-break the same set could still collapse to two
 * different strings — the reproducibility MD-315 established.
 */
export const slotsInChronologicalOrder = <T extends SlotLike>(
  slots: readonly T[] | null | undefined
): T[] => {
  const all = [...(slots ?? [])];
  if (!all.length || !all.every((slot) => slot?.start)) return slotsInNamingOrder(all);
  const byLabel = (a: T, b: T) => slotTherapyLabel(a).localeCompare(slotTherapyLabel(b), "de");
  const startedAt = (slot: T) => new Date(slot.start as string | Date).getTime();
  return all.sort((a, b) => {
    const gap = startedAt(a) - startedAt(b);
    // An unparseable start yields NaN, which must not be allowed to decide the
    // order — fall to the label so the result stays total and reproducible.
    return Number.isNaN(gap) || gap === 0 ? byLabel(a, b) : gap;
  });
};

/**
 * THE way to collapse an appointment's therapies into one string. Every label,
 * drag ghost and warning message must go through this rather than mapping and
 * joining its own way — that divergence is what made the same appointment read
 * differently on every surface.
 *
 * Reads in CHRONOLOGICAL order, so the string says what happens first (MD-411).
 * Routing every surface through one axis is what keeps them agreeing with each
 * other, which is the whole reason this function exists; fixing one surface on
 * its own would reopen MD-315. An unscheduled appointment has no chronology, so
 * there it still reads in naming order.
 *
 * A Doppelbehandlung is two slots on one line and deliberately still reads as
 * two ("KG, KG"): the label says what is delivered, and collapsing it would
 * hide the second unit.
 */
export const slotTherapyList = (
  slots: readonly SlotLike[] | null | undefined,
  separator = ", "
): string =>
  slotsInChronologicalOrder(slots)
    .map(slotTherapyLabel)
    .filter(Boolean)
    .join(separator);

/**
 * The IDENTITY of an appointment's therapy set — for grouping appointments that
 * carry the same therapies into one shelf stack.
 *
 * Sorted, so it is order-INDEPENDENT: this is the one place that must not care
 * how the set is displayed, only which set it is. Grouping on the raw array
 * order split "KG + Wärme" and "Wärme + KG" into two stacks of the same thing
 * (MD-315).
 *
 * Falls back to the therapy id for a slot with no prescription line (a sale),
 * so two different sales do not collide on an empty key.
 */
export const canonicalTherapyKey = (slots: readonly SlotLike[] | null | undefined): string =>
  (slots ?? [])
    .map((slot) => {
      const lineId = slot?.therapyRx?.id;
      if (lineId) return lineId;
      const therapyId = slotTherapy(slot)?.id;
      return therapyId ? `therapy-${therapyId}` : "";
    })
    .filter(Boolean)
    .sort()
    .join("+");
/**
 * The therapy a LAYOUT decision reads: the resolved LINE ITEM, or nothing.
 *
 * Deliberately not slotTherapy(). That answers a DISPLAY question and prefers
 * the prescribed term on an unscheduled slot — correct for a label, wrong here,
 * because duration and passivity are line-item facts a term does not carry.
 * Asking the term for them is how the WaitList path laid every slot out at the
 * 30-minute placeholder and shifted by zero (MD-314): the term branch has no
 * duration at all, and it synthesises passivity from an abbreviation list that
 * both misses WAERME and wrongly claims TA.
 *
 * An unscheduled slot's line item is history — "what it was, under that
 * therapist's credentials" — and under a different therapist the term could
 * resolve elsewhere. It is still the only real evidence on the slot, it is what
 * the drag payload already carries, and the backend re-resolves and re-clamps
 * either way. Undefined means "not known here", and callers must treat it as
 * such rather than substituting a guess.
 *
 * A sale's line item is its sold service, which is never re-resolved
 * (MD-724, see soldService).
 */
export const layoutTherapy = (
  slot: SlotLike | null | undefined
): TherapyDetailsDto | undefined => slot?.therapy ?? soldService(slot);

/** How one appointment's slots sit around a drop point. See layoutSlots. */
export interface SlotLayout<T> {
  /** Scheduling order: supplements first, alphabetical within each side. */
  ordered: T[];
  /** Passive preparations that run BEFORE the drop point (in `ordered` order). */
  leadingPassive: T[];
  /** Everything else — the stretch that starts ON the drop point. */
  atDropPoint: T[];
}

/**
 * The ONE decision both halves of a drop must share: which slots land on the
 * drop point, and which run ahead of it.
 *
 * The drag ghost draws how much time the appointment claims AT the drop point;
 * the layout writes the windows. MD-314 is what happens when those two answer
 * differently — the ghost drew the KG alone (20 min, "this gap fits") while the
 * layout wrote PA+KG from the drop point (45 min) straight over the next
 * appointment. Neither number was wrong on its own; they were computed from
 * different notions of passivity. Both now call this.
 *
 * Passivity is read from the resolved LINE ITEM only. A term-only slot has no
 * line item and therefore no known passivity, and it is treated as active
 * rather than moved on a guess: WAERME resolves to HL/PA (passive, 25 min) or
 * HR (active, 5 min) depending on the therapist, so the prescribed term simply
 * cannot answer. Such a slot lands at the drop point, and the backend — the
 * only party that has resolved the line item — corrects it from there.
 *
 * An appointment with no active slot shifts nothing: there is no main therapy
 * to lead into. This mirrors the backend's own layout, which clamps
 * `firstNonHeatIndex` to 0 (appointments-templates.logic.ts).
 */
export const layoutSlots = <T extends SlotLike>(slots: readonly T[]): SlotLayout<T> => {
  // Ordering is slotsInDeliveryOrder's job, not a second copy of it. This used
  // to sort on `therapyRx.order` — a column nothing in the API ever writes, so
  // the comparator was always 0 and each side kept whatever order the API
  // happened to return. MD-315 replaced that with the alphabetical tie-break
  // for exactly this reason; a layout that is not reproducible re-orders itself
  // whenever a row is updated.
  const ordered = slotsInDeliveryOrder(slots);

  // Passivity from the line item only — never from slotTherapy's term branch.
  const firstActive = ordered.findIndex(
    (slot) => !isPassiveTherapy(layoutTherapy(slot))
  );
  // No active slot: nothing to lead into, so nothing shifts.
  const leadCount = firstActive < 0 ? 0 : firstActive;

  return {
    ordered,
    leadingPassive: ordered.slice(0, leadCount),
    atDropPoint: ordered.slice(leadCount),
  };
};
